import React, { ComponentProps, useCallback } from 'react';
import Select, { SingleValue } from 'react-select';

import { getSelectStyle } from '../../../../common/components/Select/select-style';
import { CityResponse } from '../../../../common/models/city';
import { CountryResponse } from '../../../../common/models/country';
import { useLocalizationContext } from '../../../../contexts/LocalizationContext/useLocalizationContext.hook';
import { useCities } from '../../../../services/cities';

type CitySelectProps = Omit<
  ComponentProps<typeof Select>,
  'options' | 'isLoading' | 'getOptionLabel' | 'getOptionValue' | 'onChange'
> & {
  selectedValue: CityResponse | null;
  onSelect: (value: CityResponse | null) => void;
  country?: CountryResponse | null;
  borderColor?: string;
};

export const CitySelect = ({ country, selectedValue, onSelect, borderColor, ...props }: CitySelectProps) => {
  const {
    data: paginatedCities,
    isLoading: isLoadingCities,
    isFetching: isFetchingCities,
  } = useCities({ limit: 0, page: 1, q: '', countryId: country?.id }, !!country);
  const { language: languageFromContext } = useLocalizationContext();
  const handleChange = useCallback((value: SingleValue<CityResponse>) => onSelect(value), [onSelect]);
  const handleOptionLabel = useCallback(
    (option: CityResponse) => option.translations[languageFromContext],
    [languageFromContext],
  );
  const handleOptionValue = useCallback((option: CityResponse) => option.id, []);

  return (
    <Select<CityResponse>
      isLoading={isLoadingCities || isFetchingCities}
      isDisabled={props.isDisabled}
      getOptionLabel={handleOptionLabel}
      getOptionValue={handleOptionValue}
      onChange={handleChange}
      value={selectedValue}
      options={paginatedCities?.items}
      menuPlacement={'auto'}
      styles={getSelectStyle<CityResponse>(borderColor)}
      placeholder={props.placeholder}
    />
  );
};
